import { useEffect, useMemo, useState } from "react";
import { IVerifiedDomain } from "@evennt-tech/api-v2-core";
import { CheckVerifiedDomainStatus } from "@evennt-tech/api-v2-external";
import { extractOrigin } from "tools/url/extractDomain";
import { useMyVerifiedDomains } from "./useMyVerifiedDomains";
import { useCheckVerifiedDomains } from "./useCheckVerifiedDomains";

export type DomainStatus =
  | "idle"
  | "loading"
  | CheckVerifiedDomainStatus
  | "unknown";

type Result = {
  status: DomainStatus;
  ownedByMe: boolean;
  record: IVerifiedDomain | null;
  domain: string | null;
};

const DEBOUNCE_MS = 400;

export const useDomainStatus = (url: string | null | undefined): Result => {
  const domain = useMemo(() => (url ? extractOrigin(url) : null), [url]);
  const [debouncedDomain, setDebouncedDomain] = useState<string | null>(domain);

  useEffect(() => {
    if (domain === debouncedDomain) {
      return;
    }
    const timeout = setTimeout(() => setDebouncedDomain(domain), DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [domain, debouncedDomain]);

  const [myDomains] = useMyVerifiedDomains();
  const { result: checkResult, isReady: checkReady } = useCheckVerifiedDomains(
    debouncedDomain ? [debouncedDomain] : []
  );

  return useMemo(() => {
    if (!url || !url.trim()) {
      return { status: "idle", ownedByMe: false, record: null, domain: null };
    }
    if (!debouncedDomain || debouncedDomain !== domain || !checkReady) {
      return {
        status: "loading",
        ownedByMe: false,
        record: null,
        domain: debouncedDomain,
      };
    }

    const ownRecord =
      myDomains?.find((d) => extractOrigin(d.url) === debouncedDomain) ?? null;
    const globalStatus = checkResult.get(debouncedDomain);

    if (globalStatus === CheckVerifiedDomainStatus.APPROVED) {
      return {
        status: CheckVerifiedDomainStatus.APPROVED,
        ownedByMe: !!ownRecord,
        record: ownRecord,
        domain: debouncedDomain,
      };
    }

    if (ownRecord) {
      if (ownRecord.isApproved === true) {
        return {
          status: CheckVerifiedDomainStatus.APPROVED,
          ownedByMe: true,
          record: ownRecord,
          domain: debouncedDomain,
        };
      }
      if (ownRecord.isApproved === false) {
        return {
          status: CheckVerifiedDomainStatus.REJECTED,
          ownedByMe: true,
          record: ownRecord,
          domain: debouncedDomain,
        };
      }
      return {
        status: CheckVerifiedDomainStatus.PENDING,
        ownedByMe: true,
        record: ownRecord,
        domain: debouncedDomain,
      };
    }
    if (globalStatus === CheckVerifiedDomainStatus.REJECTED) {
      return {
        status: CheckVerifiedDomainStatus.REJECTED,
        ownedByMe: false,
        record: null,
        domain: debouncedDomain,
      };
    }

    return {
      status: "unknown",
      ownedByMe: false,
      record: null,
      domain: debouncedDomain,
    };
  }, [url, domain, debouncedDomain, myDomains, checkResult, checkReady]);
};
